/**
 * CardGallery Component
 * Main card browsing view — filtering and sort layout aligned with reference
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchJson } from './api.js';
import CardTile from './CardTile.jsx';
import CardDetailModal from './CardDetailModal.jsx';
import {
  DOMAINS,
  SET_OPTIONS,
  RARITIES,
  CARD_TYPES,
  TAGS_OPTIONS
} from './filterOptions.js';
import { getDomainIconUrl } from './domainIcons.js';
import { MIGHT_ICON_URL, POWER_ICON_URL, ENERGY_ICON_URL } from './statIcons.js';

const SORT_OPTIONS = [
  { id: 'id', label: 'Default' },
  { id: 'collectorNumber', label: 'Number In Set' },
  { id: 'name', label: 'Name' },
  { id: 'energy', label: 'Cost' },
  { id: 'might', label: 'Might' },
  { id: 'price', label: 'Price' }
];

const SUPER_TYPE_OPTIONS = [
  { id: 'Champion', label: 'Champion' }
];

export default function CardGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCard, setSelectedCard] = useState(null);
  const showPrices = typeof window !== 'undefined'
    ? localStorage.getItem('riftbound_show_prices') !== 'false'
    : false;
  const pricePeriod = typeof window !== 'undefined'
    ? (localStorage.getItem('riftbound_price_period') || '7')
    : '7';
  const loadMoreRef = useRef(null);

  const [filters, setFilters] = useState(() => ({
    set: searchParams.get('set') || '',
    domain: searchParams.get('domain') || '',
    energy: searchParams.get('energy') || '',
    energyMin: searchParams.get('energyMin') || '',
    energyMax: searchParams.get('energyMax') || '',
    mightMin: searchParams.get('mightMin') || '',
    mightMax: searchParams.get('mightMax') || '',
    powerMin: searchParams.get('powerMin') || '',
    powerMax: searchParams.get('powerMax') || '',
    type: searchParams.get('type') || '',
    superType: searchParams.get('superType') || '',
    rarity: searchParams.get('rarity') || '',
    promo: searchParams.get('promo') || '',
    tags: searchParams.get('tags') || '',
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || 'id',
    order: searchParams.get('order') || 'asc'
  }));

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const selectedDomains = (filters.domain || '').split(',').map((s) => s.trim()).filter(Boolean);
  const toggleDomain = (label) => {
    const next = selectedDomains.includes(label)
      ? selectedDomains.filter((d) => d !== label)
      : [...selectedDomains, label];
    updateFilter('domain', next.join(','));
  };

  const hasActiveFilters =
    filters.set || filters.domain || filters.energy || filters.type ||
    filters.superType || filters.rarity || filters.promo || filters.tags || filters.search ||
    filters.energyMin || filters.energyMax || filters.mightMin || filters.mightMax ||
    filters.powerMin || filters.powerMax;

  // Sync URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Fetch cards (append when page > 1 so infinite scroll works)
  useEffect(() => {
    let cancelled = false;
    async function loadCards() {
      try {
        setLoading(true);
        if (page === 1) setError(null);
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', 48);
        Object.entries(filters).forEach(([k, v]) => {
          if (v) params.append(k, v);
        });
        if (showPrices && pricePeriod) {
          params.append('period', pricePeriod);
        }
        const data = await fetchJson(`/api/cards?${params.toString()}`);
        if (cancelled) return;
        const items = data.items || [];
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
        setCards((prev) => (page === 1 ? items : [...prev, ...items]));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load cards:', err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadCards();
    return () => { cancelled = true; };
  }, [page, filters, showPrices, pricePeriod]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      set: '', domain: '', energy: '', energyMin: '', energyMax: '',
      mightMin: '', mightMax: '', powerMin: '', powerMax: '',
      type: '', superType: '', rarity: '', promo: '', tags: '', search: '',
      sort: 'id', order: 'asc'
    });
    setPage(1);
  };

  // Infinite scroll: load next page when sentinel enters viewport
  const loadMore = useCallback(() => {
    if (loading || page >= totalPages) return;
    setPage((p) => p + 1);
  }, [loading, page, totalPages]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  if (error) {
    return (
      <div className="gallery-error">
        <h2>Error Loading Cards</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="card-gallery">
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}

      <aside className="gallery-sidebar">
        <div className="gallery-header">
          <h1>Cards</h1>
          <p className="gallery-stats">
            {loading ? 'Loading...' : `${total} cards found`}
          </p>
        </div>

        <div className="gallery-filters">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search by name, ID, set, type…"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button search-button-icon" aria-label="Search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </form>

          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Set</label>
            <select
              value={filters.set}
              onChange={(e) => updateFilter('set', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All sets</option>
              {SET_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-vertical">
            <span className="filter-label">Domain <span className="filter-or-hint">(OR)</span></span>
            <div className="filter-domain-icons">
              {DOMAINS.filter((d) => d.id !== 'colourless' && d.id !== 'colorless').map((d) => {
                const iconUrl = getDomainIconUrl(d.id);
                const selected = selectedDomains.includes(d.label);
                return (
                  <button
                    key={d.id}
                    type="button"
                    className={`filter-domain-btn ${selected ? 'selected' : ''}`}
                    onClick={() => toggleDomain(d.label)}
                    title={d.label}
                    aria-pressed={selected}
                  >
                    {iconUrl ? (
                      <img src={iconUrl} alt="" className="filter-domain-icon" aria-hidden />
                    ) : (
                      <span className="filter-domain-symbol">{d.label.charAt(0)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-group filter-group-vertical filter-stat-range">
            <label className="filter-label" htmlFor="filter-energy-max">
              <img src={ENERGY_ICON_URL} alt="" className="filter-stat-label-icon" aria-hidden />
              Cost ≤
            </label>
            <div className="filter-range-row">
              <input
                id="filter-energy-max"
                type="range"
                min={0}
                max={8}
                value={filters.energyMax === '' ? 8 : Math.min(Number(filters.energyMax) || 8, 8)}
                onChange={(e) => updateFilter('energyMax', e.target.value === '8' ? '' : e.target.value)}
                className="filter-range"
              />
              <span className="filter-range-value">{filters.energyMax === '' ? 'any' : filters.energyMax}</span>
            </div>
          </div>
          <div className="filter-group filter-group-vertical filter-stat-range">
            <label className="filter-label" htmlFor="filter-might-max">
              <img src={MIGHT_ICON_URL} alt="" className="filter-stat-label-icon" aria-hidden />
              Might ≤
            </label>
            <div className="filter-range-row">
              <input
                id="filter-might-max"
                type="range"
                min={0}
                max={10}
                value={filters.mightMax === '' ? 10 : Math.min(Number(filters.mightMax) || 10, 10)}
                onChange={(e) => updateFilter('mightMax', e.target.value === '10' ? '' : e.target.value)}
                className="filter-range"
              />
              <span className="filter-range-value">{filters.mightMax === '' ? 'any' : filters.mightMax}</span>
            </div>
          </div>
          <div className="filter-group filter-group-vertical filter-stat-range">
            <label className="filter-label" htmlFor="filter-power-max">
              <img src={POWER_ICON_URL} alt="" className="filter-stat-label-icon" aria-hidden />
              Power ≤
            </label>
            <div className="filter-range-row">
              <input
                id="filter-power-max"
                type="range"
                min={0}
                max={10}
                value={filters.powerMax === '' ? 10 : Math.min(Number(filters.powerMax) || 10, 10)}
                onChange={(e) => updateFilter('powerMax', e.target.value === '10' ? '' : e.target.value)}
                className="filter-range"
              />
              <span className="filter-range-value">{filters.powerMax === '' ? 'any' : filters.powerMax}</span>
            </div>
          </div>

          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Card Type</label>
            <select
              value={filters.type}
              onChange={(e) => updateFilter('type', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All</option>
              {CARD_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Super Type</label>
            <select
              value={filters.superType}
              onChange={(e) => updateFilter('superType', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All</option>
              {SUPER_TYPE_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Rarity</label>
            <select
              value={filters.rarity}
              onChange={(e) => updateFilter('rarity', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All</option>
              {RARITIES.map((r) => (
                <option key={r.id} value={r.label}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Promo</label>
            <select
              value={filters.promo}
              onChange={(e) => updateFilter('promo', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All</option>
              <option value="yes">Promo only</option>
              <option value="no">Non-promo only</option>
            </select>
          </div>
          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Tags</label>
            <select
              value={filters.tags}
              onChange={(e) => updateFilter('tags', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="">All</option>
              {TAGS_OPTIONS.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Sort By</label>
            <select
              value={filters.sort}
              onChange={(e) => updateFilter('sort', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-group-vertical">
            <label className="filter-label">Order</label>
            <select
              value={filters.order}
              onChange={(e) => updateFilter('order', e.target.value)}
              className="filter-select filter-dropdown filter-select-compact"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <div className="filter-sidebar-actions">
            {hasActiveFilters && (
              <button type="button" onClick={clearFilters} className="clear-filters-button">
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </aside>

      <main className="gallery-main">
      {loading && page === 1 ? (
        <div className="gallery-loading">
          <p>Loading cards...</p>
        </div>
      ) : (
        <>
          <div className="cards-grid">
            {cards.map((card) => (
              <CardTile
                key={card.id || card._id}
                card={card}
                onClick={() => setSelectedCard(card)}
                showPrice={showPrices}
                pricePeriod={pricePeriod}
              />
            ))}
          </div>

          {page < totalPages && (
            <div ref={loadMoreRef} className="gallery-pagination" aria-hidden>
              <button
                onClick={loadMore}
                disabled={loading}
                className="load-more-button"
              >
                {loading ? 'Loading...' : `Load More (Page ${page + 1} of ${totalPages})`}
              </button>
            </div>
          )}

          {cards.length === 0 && !loading && (
            <div className="no-results">
              <p>No cards found matching your filters.</p>
            </div>
          )}
        </>
      )}
      </main>
    </div>
  );
}
